import { cn } from 'cn';
import { type ReactNode, useId } from 'react';

import { type UUID } from '~/types/common';

import { type ContractListItem, type ContractStatus } from '~/data/contract';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { MultiSelectContracts, type Option } from '../SelectServerDriven';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly buyerCompanyId?: UUID;
  readonly classNameErrors?: string;
  readonly formatOptionLabel?: (option: Option<ContractListItem>) => ReactNode;
  readonly hint?: string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<ContractListItem>) => boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly sellerCompanyId?: UUID;
  readonly status?: ContractStatus[];
  readonly testId?: string;
} & ComponentStyling;

export const MultiSelectContractsField = ({
  buyerCompanyId,
  className,
  classNameErrors,
  formatOptionLabel,
  hint,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  label,
  placeholder,
  sellerCompanyId,
  status,
  style,
  testId,
}: P) => {
  const field = useFieldContext();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(field.state.meta.errors?.length);

  const id = useId();

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <MultiSelectContracts
        buyerCompanyId={buyerCompanyId}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMultiSelect={isMultiSelect}
        isRemovableValue={isRemovableValue}
        placeholder={placeholder}
        sellerCompanyId={sellerCompanyId}
        status={status}
        value={field.state.value ?? []}
        onChange={(contracts) => {
          const isArray = Array.isArray(contracts);
          if (isArray) {
            field.handleChange(contracts.map(({ id }) => id));
          } else {
            field.handleChange(contracts?.id ?? '');
          }
        }}
      />
      {hasErrors ? (
        <ValidationError className={classNameErrors}>
          {unique(field.state.meta.errors.map(({ message }) => message)).join(
            ', ',
          )}
        </ValidationError>
      ) : null}
    </div>
  );
};
